import { useState } from "react";
import {
  AlertTriangle,
  Ban,
  Box,
  Clock3,
  Database,
  EyeOff,
  FileText,
  GitBranch,
  ChevronRight,
  Github,
  Loader2,
  MapPin,
  Monitor,
  ScanSearch,
  Server,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { Badge } from "../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Skeleton } from "../../ui/skeleton";
import {
  SecretDiscoveryData,
  SecretDiscoveryLocation,
  SecretDiscoveryScanState,
  SecretDiscoverySourceType,
} from "./secretDiscoveryService";

const SCAN_STATE_PILL_CLASS: Record<SecretDiscoveryScanState, string> = {
  IN_PROGRESS:
    "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-300",
  COMPLETED:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300",
  COMPLETED_WITH_ERRORS:
    "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300",
  FAILED:
    "border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300",
  CANCELLED:
    "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-300",
};

const SCAN_STATE_ICON: Record<SecretDiscoveryScanState, typeof ShieldCheck> = {
  IN_PROGRESS: Loader2,
  COMPLETED: ShieldCheck,
  COMPLETED_WITH_ERRORS: AlertTriangle,
  FAILED: XCircle,
  CANCELLED: Ban,
};

interface SecretDiscoveryProps {
  data?: SecretDiscoveryData | null;
  isLoading?: boolean;
}

const DISCOVERY_FILTER_SOURCES: SecretDiscoverySourceType[] = [
  "compute",
  "storage",
  "database",
  "kubernetes",
];

const SOURCE_CONFIG: Record<
  SecretDiscoverySourceType,
  { label: string; icon: typeof Server; colorClass: string }
> = {
  compute: {
    label: "Compute",
    icon: Server,
    colorClass: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  },
  storage: {
    label: "Buckets",
    icon: Box,
    colorClass: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400",
  },
  database: {
    label: "Databases",
    icon: Database,
    colorClass: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  },
  kubernetes: {
    label: "Kubernetes",
    icon: FileText,
    colorClass: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
  },
  endpoint: {
    label: "Endpoints",
    icon: Monitor,
    colorClass: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  },
  github: {
    label: "GitHub",
    icon: Github,
    colorClass: "bg-slate-500/10 text-slate-700 dark:text-slate-300",
  },
  gitlab: {
    label: "GitLab",
    icon: GitBranch,
    colorClass: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
  },
};

const INITIAL_ROWS_PER_RESOURCE = 25;

function splitPath(path?: string): { name: string; directory: string } {
  if (!path) return { name: "", directory: "" };
  const index = path.lastIndexOf("/");
  if (index < 0) return { name: path, directory: "" };
  return { name: path.slice(index + 1), directory: path.slice(0, index + 1) };
}

function DiscoveryFindingRow({ location }: { location: SecretDiscoveryLocation }) {
  const isFile = location.locationType === "FILE";
  const fullPath = isFile ? location.locationPath || location.location : location.location;
  const { name, directory } = splitPath(isFile ? fullPath : "");
  const hasInferredKey = Boolean(location.storedAs && location.storedAs !== "-");
  const storedAs = hasInferredKey
    ? (location.storedAs as string)
    : isFile && name
      ? name
      : location.location;

  return (
    <div
      className="grid gap-4 px-5 py-3 transition-colors hover:bg-muted/20 lg:grid-cols-[minmax(170px,0.8fr)_minmax(280px,1.7fr)_minmax(150px,0.55fr)] lg:items-center"
      data-testid={`discovery-location-${location.id}`}
    >
      <div className="min-w-0">
        <div className="text-[11px] font-medium text-muted-foreground">Stored As</div>
        <div className="mt-1 truncate font-mono text-xs font-semibold text-foreground" title={storedAs}>
          {storedAs}
        </div>
        {!hasInferredKey ? (
          <div className="mt-0.5 text-[11px] font-normal text-muted-foreground">
            Bare value in file
          </div>
        ) : null}
      </div>

      <div className="min-w-0">
        <div className="text-[11px] font-medium text-muted-foreground">Location</div>
        <div className="mt-1 flex min-w-0 items-center gap-1.5">
          {isFile
            ? <FileText className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
            : <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />}
          <span className="truncate font-mono text-xs text-muted-foreground" title={fullPath}>
            {isFile && name ? (
              <>
                {directory}
                <span className="font-semibold text-foreground">{name}</span>
              </>
            ) : (
              <span className="font-sans text-foreground/85">{fullPath}</span>
            )}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground lg:justify-end">
        {location.lineNumber ? <span>line {location.lineNumber}</span> : null}
        {location.matchEncoding && location.matchEncoding !== "PLAINTEXT" ? (
          <Badge variant="secondary" className="h-5 text-[10px]">{location.matchEncoding}</Badge>
        ) : null}
        <span className="inline-flex items-center gap-1">
          <Clock3 className="h-3 w-3" />
          {location.lastSeenAt}
        </span>
      </div>
    </div>
  );
}

interface ResourceGroup {
  key: string;
  resourceName: string;
  sourceType: SecretDiscoverySourceType;
  sourceLabel: string;
  region: string;
  locations: SecretDiscoveryLocation[];
}

function ResourceGroupCard({ group }: { group: ResourceGroup }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const source = SOURCE_CONFIG[group.sourceType];
  const SourceIcon = source.icon;
  const visible = showAll ? group.locations : group.locations.slice(0, INITIAL_ROWS_PER_RESOURCE);
  const hiddenCount = group.locations.length - visible.length;

  return (
    <div data-testid={`discovery-resource-${group.resourceName}`}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-muted/20"
        data-testid={`discovery-resource-toggle-${group.resourceName}`}
      >
        <ChevronRight
          className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`}
        />
        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${source.colorClass}`}>
          <SourceIcon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-foreground" title={group.resourceName}>
            {group.resourceName}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {group.sourceLabel}
            {group.region && group.region !== "-" ? ` · ${group.region}` : ""}
          </div>
        </div>
        <Badge className="h-6 flex-shrink-0 bg-primary text-[11px] text-primary-foreground">
          {group.locations.length} {group.locations.length === 1 ? "location" : "locations"}
        </Badge>
      </button>

      {isOpen ? (
        <div className="divide-y divide-border/40 border-t border-border/40 bg-muted/10">
          {visible.map((location) => (
            <DiscoveryFindingRow key={location.id} location={location} />
          ))}
          {hiddenCount > 0 ? (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="w-full px-5 py-3 text-xs font-medium text-primary hover:bg-muted/30"
              data-testid={`discovery-resource-show-all-${group.resourceName}`}
            >
              Show {hiddenCount} more {hiddenCount === 1 ? "location" : "locations"}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function groupByResource(locations: SecretDiscoveryLocation[]): ResourceGroup[] {
  const groups = new Map<string, ResourceGroup>();
  for (const location of locations) {
    const key = `${location.sourceType}:${location.resourceName}`;
    const existing = groups.get(key);
    if (existing) {
      existing.locations.push(location);
      continue;
    }
    groups.set(key, {
      key,
      resourceName: location.resourceName,
      sourceType: location.sourceType,
      sourceLabel: location.sourceLabel,
      region: location.region,
      locations: [location],
    });
  }
  return Array.from(groups.values()).sort((a, b) => b.locations.length - a.locations.length);
}

export default function SecretDiscovery({ data, isLoading = false }: SecretDiscoveryProps) {
  const [sourceFilter, setSourceFilter] = useState<"all" | SecretDiscoverySourceType>("all");

  if (isLoading) {
    return (
      <div className="space-y-5" data-testid="secret-discovery-loading">
        <Skeleton className="h-44 w-full rounded-2xl" />
        <Skeleton className="h-80 w-full rounded-2xl" />
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="cea-card-surface border-dashed">
        <CardContent className="flex min-h-72 flex-col items-center justify-center p-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <ScanSearch className="h-6 w-6" />
          </div>
          <h2 className="mt-4 text-lg font-semibold">No discovered copies</h2>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            The latest scan has not reported this secret outside its managed secret store.
          </p>
          <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/30 px-3 py-1.5 text-xs text-muted-foreground">
            <EyeOff className="h-3.5 w-3.5" /> Secret values are never displayed
          </div>
        </CardContent>
      </Card>
    );
  }

  const sourceCounts = data.locations.reduce<Record<SecretDiscoverySourceType, number>>(
    (counts, location) => {
      counts[location.sourceType] += 1;
      return counts;
    },
    { compute: 0, storage: 0, database: 0, kubernetes: 0, endpoint: 0, github: 0, gitlab: 0 },
  );
  const filterSources = DISCOVERY_FILTER_SOURCES.map(
    (type) => [type, sourceCounts[type]] as [SecretDiscoverySourceType, number],
  );
  const filteredLocations = sourceFilter === "all"
    ? data.locations
    : data.locations.filter((location) => location.sourceType === sourceFilter);
  const resourceGroups = groupByResource(filteredLocations);

  return (
    <div className="space-y-5" data-testid="secret-discovery-content">
      <Card className="cea-card-surface relative overflow-hidden">
        <div className="pointer-events-none absolute right-0 top-0 h-40 w-40 rounded-full bg-primary/5 blur-3xl" />
        <CardContent className="relative p-0">
          <div className="flex flex-col justify-between gap-5 p-6 md:flex-row md:items-center">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15">
                <ScanSearch className="h-6 w-6" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold text-foreground">Secret footprint</h2>
                  <Badge className="h-5 bg-primary text-[10px] text-primary-foreground">
                    {data.locations.length} locations
                  </Badge>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  Exposed copies found outside the managed secret store
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 md:justify-end">
              {(() => {
                const scanState = data.scanState ?? "COMPLETED";
                const StateIcon = SCAN_STATE_ICON[scanState];
                return (
                  <div
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${SCAN_STATE_PILL_CLASS[scanState]}`}
                    data-testid="discovery-scan-status"
                  >
                    <StateIcon
                      className={`h-3.5 w-3.5 ${scanState === "IN_PROGRESS" ? "animate-spin" : ""}`}
                    />{" "}
                    {data.scanStatus}
                  </div>
                );
              })()}
              <span className="text-xs text-muted-foreground">Scanned {data.scannedAt}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-border/50 bg-muted/15 px-6 py-4">
            <button
              type="button"
              onClick={() => setSourceFilter("all")}
              aria-pressed={sourceFilter === "all"}
              data-testid="discovery-filter-all"
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs shadow-sm transition-colors ${
                sourceFilter === "all"
                  ? "border-primary/30 bg-primary/10 text-primary ring-1 ring-primary/10"
                  : "border-border/50 bg-card text-foreground hover:border-primary/30 hover:bg-primary/5"
              }`}
            >
              <ScanSearch className="h-3.5 w-3.5" />
              <span className="font-medium">All</span>
              <span className="font-semibold">{data.locations.length}</span>
            </button>
            {filterSources.map(([type, count]) => {
              const source = SOURCE_CONFIG[type];
              const Icon = source.icon;
              const isActive = sourceFilter === type;
              const isEmpty = count === 0;
              return (
                <button
                  key={type}
                  type="button"
                  disabled={isEmpty}
                  onClick={() => setSourceFilter(isActive ? "all" : type)}
                  aria-pressed={isActive}
                  title={isEmpty ? `No ${source.label.toLowerCase()} findings for this secret` : undefined}
                  data-testid={`discovery-filter-${type}`}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs shadow-sm transition-colors ${
                    isEmpty
                      ? "cursor-not-allowed border-border/40 bg-muted/30 text-muted-foreground/50 shadow-none"
                      : isActive
                        ? "border-primary/30 bg-primary/10 text-primary ring-1 ring-primary/10"
                        : "border-border/50 bg-card text-foreground hover:border-primary/30 hover:bg-primary/5"
                  }`}
                >
                  <Icon
                    className={`h-3.5 w-3.5 ${
                      isEmpty ? "text-muted-foreground/40" : isActive ? "text-primary" : "text-muted-foreground"
                    }`}
                  />
                  <span className="font-medium">{source.label}</span>
                  <span className={`font-semibold ${isEmpty ? "text-muted-foreground/50" : "text-primary"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
            <div className="ml-0 inline-flex items-center gap-1.5 text-xs text-muted-foreground sm:ml-auto">
              <EyeOff className="h-3.5 w-3.5" /> Values hidden by design
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="cea-card-surface">
        <CardHeader className="flex-row items-center justify-between space-y-0 border-b border-border/50 px-5 py-4">
          <div>
            <CardTitle className="text-base font-semibold">Affected resources</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              {resourceGroups.length} {resourceGroups.length === 1 ? "resource holds" : "resources hold"}{" "}
              {filteredLocations.length} {filteredLocations.length === 1 ? "copy" : "copies"} of this secret.
              Expand a resource to see where.
            </p>
          </div>
          <Badge variant="secondary" className="h-6 min-w-8 justify-center text-xs">
            {sourceFilter === "all"
              ? resourceGroups.length
              : `${resourceGroups.length} of ${groupByResource(data.locations).length}`}
          </Badge>
        </CardHeader>
        <CardContent className="divide-y divide-border/50 p-0">
          {resourceGroups.map((group) => (
            <ResourceGroupCard key={group.key} group={group} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
